import React, { useState } from "react";
import { api, formatErr, fmtDate } from "../api";
import {
  InstagramLogo, X, Heart, ChatCircle, Users, Stack, TrendUp,
  WarningCircle, ArrowSquareOut, VideoCamera, Image as ImageIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/** Reusable trigger button — drop this next to a creator row/card. */
export const InstagramInsightsButton = ({ onClick, size = "sm", testIdSuffix }) => {
  const cls = size === "sm"
    ? "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#8B5CF6]/15 to-[#EC4899]/15 border border-[#8B5CF6]/30 text-[#EC4899] hover:from-[#8B5CF6]/25 hover:to-[#EC4899]/25 hover:text-white text-xs font-bold transition"
    : "inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white font-bold text-sm hover:opacity-90 transition glow-purple";
  return (
    <button type="button" onClick={onClick} data-testid={`ig-insights-btn-${testIdSuffix || "default"}`} className={cls}>
      <InstagramLogo size={size === "sm" ? 12 : 14} weight="fill" /> Instagram Insights
    </button>
  );
};

const fmtCount = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(1) + "M";
  if (v >= 1000) return (v / 1000).toFixed(1) + "k";
  return String(v);
};

const engagementColor = (r) => {
  if (r == null) return "#9CA3AF";
  if (r >= 3) return "#A3E635";
  if (r >= 1) return "#22D3EE";
  return "#F59E0B";
};

/** Modal panel — controlled. Pass { onClose }. Shows the connected account's
 * profile summary + last 10 posts with per-post engagement rate. */
export const InstagramInsightsPanel = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api.get("/integrations/instagram/media")
      .then(r => setData(r.data))
      .catch(e => setLoadError(formatErr(e)))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const posts = data?.posts || [];
  const windowLabel = data?.window === "last_30_days" ? "last 30 days" : `last ${posts.length} posts`;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 sm:p-8 overflow-y-auto" onClick={onClose} data-testid="ig-insights-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative glass max-w-3xl w-full p-5 sm:p-7 my-auto"
        onClick={e => e.stopPropagation()}
        style={{ background: "linear-gradient(180deg, rgba(236,72,153,0.08), rgba(15,15,20,0.85))" }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#EC4899] to-[#8B5CF6] grid place-items-center glow-purple">
              <InstagramLogo size={18} weight="fill" color="white" />
            </div>
            <div className="text-white font-extrabold text-lg leading-tight">Instagram Insights</div>
          </div>
          <button onClick={onClose} data-testid="ig-insights-close" className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 grid place-items-center text-white">
            <X size={16} weight="bold" />
          </button>
        </div>

        {loading ? (
          <div className="space-y-3 py-6">
            <div className="h-20 rounded-2xl bg-white/5 animate-pulse" />
            <div className="h-64 rounded-2xl bg-white/5 animate-pulse" />
          </div>
        ) : loadError ? (
          <div className="glass-soft p-8 flex flex-col items-center gap-3 text-center">
            <WarningCircle size={32} weight="fill" className="text-[#EC4899]" />
            <div className="text-white font-bold">Couldn't load Instagram data</div>
            <div className="text-muted text-sm">{loadError}</div>
            <button onClick={load} className="btn-primary text-sm px-4 py-2 mt-2">Retry</button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Account summary */}
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center flex-shrink-0">
                <InstagramLogo size={22} weight="fill" color="white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white font-bold text-base truncate" data-testid="ig-username">@{data.username || "—"}</div>
                <div className="text-xs text-muted">Connected account · {windowLabel}</div>
              </div>
              <div className="flex gap-4 flex-shrink-0">
                <Stat icon={Users} label="Followers" value={fmtCount(data.followers_count)} />
                <Stat icon={Stack} label="Posts" value={fmtCount(data.media_count)} />
                <Stat
                  icon={TrendUp}
                  label="Engagement"
                  value={data.overall_engagement_rate != null ? `${data.overall_engagement_rate}%` : "—"}
                  color={engagementColor(data.overall_engagement_rate)}
                />
              </div>
            </div>

            <div className="text-[11px] text-muted italic px-1">
              Showing the platform's connected test account. Per-creator Instagram linking is coming soon.
            </div>

            {/* Posts list */}
            {posts.length === 0 ? (
              <div className="text-center text-muted text-sm py-8">No posts found.</div>
            ) : (
              <div className="space-y-2" data-testid="ig-posts-list">
                {posts.map(p => (
                  <PostRow key={p.id} p={p} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PostRow = ({ p }) => {
  const MediaIcon = p.media_type === "VIDEO" ? VideoCamera : ImageIcon;
  return (
    <a
      href={p.permalink}
      target="_blank"
      rel="noreferrer"
      className="flex items-start gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition group"
      data-testid={`ig-post-${p.id}`}
    >
      <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 grid place-items-center flex-shrink-0 text-muted">
        <MediaIcon size={16} weight="bold" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white/90 line-clamp-2 leading-snug">{p.caption || "(no caption)"}</div>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted">
          <span className="flex items-center gap-1"><Heart size={12} weight="fill" className="text-[#EC4899]" /> {fmtCount(p.likes)}</span>
          <span className="flex items-center gap-1"><ChatCircle size={12} weight="fill" /> {fmtCount(p.comments)}</span>
          <span>{fmtDate(p.posted_at)}</span>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <div className="flex items-center gap-1 text-sm font-black" style={{ color: engagementColor(p.engagement_rate) }}>
          <TrendUp size={13} weight="bold" />
          {p.engagement_rate != null ? `${p.engagement_rate}%` : "—"}
        </div>
        <ArrowSquareOut size={13} className="text-muted opacity-0 group-hover:opacity-100 transition" />
      </div>
    </a>
  );
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="text-center">
    <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-muted font-bold">
      <Icon size={11} weight="bold" /> {label}
    </div>
    <div className="text-lg font-black" style={{ color: color || "white" }}>{value}</div>
  </div>
);

/**
 * Convenience hook, same shape as useAIAudit():
 *   const { openInsights, InsightsModal } = useInstagramInsights();
 *   <InstagramInsightsButton onClick={openInsights} />
 *   {InsightsModal}
 */
export const useInstagramInsights = () => {
  const [open, setOpen] = useState(false);
  const openInsights = () => setOpen(true);
  const closeInsights = () => setOpen(false);
  const InsightsModal = open ? <InstagramInsightsPanel onClose={closeInsights} /> : null;
  return { open, openInsights, closeInsights, InsightsModal };
};
